import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/seo';
import { useMSPs } from '@/hooks/useMSPs';

import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { OrganizationSchema } from '@/components/seo/OrganizationSchema';
// Above-the-fold: ship in initial bundle for fast LCP.
import { HomeHeroSection } from '@/components/home/HomeHeroSection';
import { MSPSearchBox } from '@/components/home/MSPSearchBox';
import { DeferUntilNear } from '@/components/layout/DeferUntilNear';

// Below-the-fold: code-split so they don't block initial render.
const InfoBlocksSection = lazy(() => import('@/components/home/InfoBlocksSection').then(m => ({ default: m.InfoBlocksSection })));
const FeaturedMSPsSection = lazy(() => import('@/components/home/FeaturedMSPsSection').then(m => ({ default: m.FeaturedMSPsSection })));
const WhyChooseSection = lazy(() => import('@/components/home/WhyChooseSection').then(m => ({ default: m.WhyChooseSection })));
const AcademyPreviewSection = lazy(() => import('@/components/home/AcademyPreviewSection').then(m => ({ default: m.AcademyPreviewSection })));
const SeoLinksSection = lazy(() => import('@/components/home/SeoLinksSection').then(m => ({ default: m.SeoLinksSection })));
const CTASection = lazy(() => import('@/components/home/CTASection').then(m => ({ default: m.CTASection })));

// Skeleton components for loading states
const MSPSkeletonSection = () => (
  <section className="bg-muted py-12 lg:py-16">
    <div className="container mx-auto px-4">
      <div className="text-center mb-8">
        <Skeleton className="h-8 w-64 mx-auto mb-2" />
        <Skeleton className="h-5 w-96 mx-auto" />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card rounded-xl p-6 border border-border">
            <div className="flex items-start gap-4 mb-4">
              <Skeleton className="h-16 w-16 rounded-lg shrink-0" />
              <div className="flex-1">
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2 mb-1" />
                <Skeleton className="h-4 w-1/3" />
              </div>
            </div>
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-2/3 mb-4" />
            <div className="flex gap-2">
              <Skeleton className="h-6 w-16 rounded-full" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ArticleSkeletonSection = () => (
  <section className="py-12 lg:py-16">
    <div className="container mx-auto px-4">
      <div className="text-center mb-8">
        <Skeleton className="h-8 w-48 mx-auto mb-2" />
        <Skeleton className="h-5 w-80 mx-auto" />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card rounded-xl overflow-hidden border border-border">
            <Skeleton className="h-48 w-full" />
            <div className="p-4">
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-5 w-full mb-2" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Index = () => {
  // The full MSP list (~90 kB JSON) is not needed for the first paint. It is
  // started on explicit interaction (search box focus/typing), when the
  // featured-MSP section is actually needed, or — as a fallback — when the
  // browser is idle after first paint. Interaction always wins.
  const [mspsNeeded, setMspsNeeded] = useState(false);
  const activateMsps = useCallback(() => setMspsNeeded(true), []);

  useEffect(() => {
    if (mspsNeeded) return;
    const start = () => setMspsNeeded(true);
    const w = window as any;
    if (typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(start, { timeout: 3000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const timer = w.setTimeout(start, 1500);
    return () => w.clearTimeout(timer);
  }, [mspsNeeded]);

  const { msps, loading: mspsLoading } = useMSPs({ enabled: mspsNeeded });

  // Note: WebSite + Organization schema are emitted by <OrganizationSchema />.
  // Do not duplicate them here.

  return (
    <Layout>
      <SEOHead
        title="MSP Vergelijker | Vind de beste IT-partner"
        description="Vergelijk MSP's in Nederland en vind de beste IT-partner voor jouw bedrijf. Onafhankelijk, gratis en overzichtelijk."
        canonicalUrl="/"
      />
      <OrganizationSchema />
      <HomeHeroSection
        searchSlot={
          <MSPSearchBox
            msps={msps}
            variant="embedded"
            loading={mspsLoading}
            onActivate={activateMsps}
          />
        }
      />

      <Suspense fallback={<div aria-hidden="true" style={{ minHeight: '600px' }} />}>
        <InfoBlocksSection />
      </Suspense>
      <DeferUntilNear minHeight={600} onActivate={activateMsps}>
        <Suspense fallback={<div aria-hidden="true" style={{ minHeight: '600px' }} />}>
          {mspsLoading || !msps.length ? <MSPSkeletonSection /> : <FeaturedMSPsSection msps={msps} />}
        </Suspense>
      </DeferUntilNear>
      <section className="py-8 sm:py-10">
        <div className="container mx-auto px-4">
          <div className="bg-card border border-border rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
              Niet zeker welke MSP past bij jouw bedrijf?
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mb-5 sm:mb-6 max-w-xl mx-auto">
              Beantwoord 6 korte vragen en ontvang maximaal 3 matches — onafhankelijk, gratis en binnen 2 minuten.
            </p>
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/wizard">Start de wizard</Link>
            </Button>
          </div>
        </div>
      </section>
      <Suspense fallback={<div aria-hidden="true" style={{ minHeight: '500px' }} />}>
        <WhyChooseSection />
      </Suspense>
      <DeferUntilNear minHeight={500}>
        <Suspense fallback={<div aria-hidden="true" style={{ minHeight: '500px' }} />}>
          <AcademyPreviewSection />
        </Suspense>
      </DeferUntilNear>
      <Suspense fallback={<div aria-hidden="true" style={{ minHeight: '300px' }} />}>
        <SeoLinksSection />
      </Suspense>
      <Suspense fallback={<div aria-hidden="true" style={{ minHeight: '300px' }} />}>
        <CTASection />
      </Suspense>
    </Layout>
  );
};

export default Index;
